import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDefaultSubscriptionSelector,
  getPetSelector,
  getLineItemSelector,
  getSubscriptionOptionsSelector,
  getAllergiesSelector,
  getHealthIssuesSelector,
} from '../../../core/selectors';
import { getUpdatedSubscriptionOptions } from '@modules/settings/core/selectors';
import {
  recalculateProductRequest,
  recalculateProductReset,
  updatePetAndSwapLineRequest,
  fetchAllergiesRequest,
  fetchHealthIssuesRequest,
} from '../../../core/actions';
import { RootState } from '@store/index';
import {
  UpdateStep,
  UpdateMode,
  PetInfoFormData,
  HealthInfoFormData,
  CustomizationFormData,
  PetProfileUpdateContextValue,
} from '../types';
import { findMatchingVariant, getDefaultRecipes } from '../utils';
import { PET_DEFAULTS } from '../types';
import {
  PetActivity,
  PetBody,
  DogStage,
  SnackLevel,
  Recipe,
  Pension,
  Cycle,
  RecalculatedProductVariant,
  UpdatePetAndSwapLineParams,
} from '../../../core/types';
import { getDisabledRecipes } from '@modules/subscriptions/utils/getDisabledRecipes';
import { getEffectiveDogStage } from '@modules/subscriptions/utils/getEffectiveDogStage';

interface UsePetProfileUpdateOptions {
  onClose: () => void;
  mode?: UpdateMode;
  dogStageOverride?: DogStage;
  initialWeightOverride?: number;
}

export function usePetProfileUpdate({
  onClose,
  mode = 'weight',
  dogStageOverride,
  initialWeightOverride,
}: UsePetProfileUpdateOptions): PetProfileUpdateContextValue {
  const dispatch = useDispatch();

  const pet = useSelector(getPetSelector);
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);
  const lineItem = useSelector(getLineItemSelector);
  const lineSubscriptionOptions = useSelector(getSubscriptionOptionsSelector);
  const draftSubscriptionOptions = useSelector(getUpdatedSubscriptionOptions);
  const petProfileUpdate = useSelector((state: RootState) => state.subscriptions.petProfileUpdate);

  const subscriptionOptions =
    mode === 'migration' && draftSubscriptionOptions
      ? draftSubscriptionOptions
      : lineSubscriptionOptions;

  const currentPetInfo: PetInfoFormData = useMemo(
    () => ({
      weight: pet?.weight || PET_DEFAULTS.WEIGHT,
      activity: (pet?.activity as PetActivity) || 'normal',
      body: (pet?.body as PetBody) || 'ideal',
      snackLevel: (pet?.snack_level as SnackLevel) || 'none',
      adultWeight: pet?.adult_weight,
    }),
    [pet],
  );

  const currentOptions: CustomizationFormData = useMemo(
    () => ({
      selectedRecipes: (subscriptionOptions?.recipes as Recipe[]) || ['CG'],
      pension: (subscriptionOptions?.pension as Pension) || 'V',
      cycle: (subscriptionOptions?.cycle as Cycle) || 14,
    }),
    [subscriptionOptions],
  );

  const [step, setStep] = useState<UpdateStep>(mode === 'migration' ? 'customize' : 'pet-info');
  const [petInfo, setPetInfo] = useState<PetInfoFormData>(() => ({
    ...currentPetInfo,
    ...(initialWeightOverride ? { weight: initialWeightOverride } : {}),
  }));
  const [healthInfo, setHealthInfo] = useState<HealthInfoFormData>(() => ({
    allergies: pet?.allergies || [],
    healthIssues: pet?.health_issues || [],
  }));
  const [customization, setCustomization] = useState<CustomizationFormData>(currentOptions);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Fetch allergies/healthIssues when in health mode
  useEffect(() => {
    if (mode === 'health') {
      dispatch(fetchAllergiesRequest());
      dispatch(fetchHealthIssuesRequest());
    }
  }, [mode, dispatch]);

  useEffect(() => {
    setPetInfo(currentPetInfo);
  }, [currentPetInfo]);

  useEffect(() => {
    setCustomization(currentOptions);
  }, [currentOptions]);

  // Reset health info when pet changes
  useEffect(() => {
    setHealthInfo({
      allergies: pet?.allergies || [],
      healthIssues: pet?.health_issues || [],
    });
  }, [pet?.allergies, pet?.health_issues]);

  // Use a ref for selectedRecipes to avoid re-triggering the effect
  // when recipes change — we only want this to run on recalculation.
  const selectedRecipesRef = useRef(customization.selectedRecipes);
  selectedRecipesRef.current = customization.selectedRecipes;

  useEffect(() => {
    if (petProfileUpdate.recalculatedProduct) {
      const { restrictions, product } = petProfileUpdate.recalculatedProduct;

      // In health mode, use the new allergies for disabled recipe calculation
      const petForDisabled =
        mode === 'health'
          ? { ...pet, allergies: healthInfo.allergies, health_issues: healthInfo.healthIssues }
          : pet;
      const allergyDisabled = petForDisabled ? getDisabledRecipes(petForDisabled as any) : [];
      const allDisabledRecipes = Array.from(
        new Set([...restrictions.disabledRecipes, ...allergyDisabled]),
      );

      const defaultRecipes = getDefaultRecipes(
        product.variants,
        allDisabledRecipes,
        selectedRecipesRef.current,
      );

      setCustomization((prev) => ({
        ...prev,
        selectedRecipes: defaultRecipes,
      }));
    }
  }, [
    petProfileUpdate.recalculatedProduct,
    pet,
    mode,
    healthInfo.allergies,
    healthInfo.healthIssues,
  ]);

  const selectedVariant: RecalculatedProductVariant | null = useMemo(() => {
    if (!petProfileUpdate.recalculatedProduct) return null;

    return findMatchingVariant(petProfileUpdate.recalculatedProduct.product.variants, {
      recipes: customization.selectedRecipes,
      pension: customization.pension,
      cycle: customization.cycle,
    });
  }, [petProfileUpdate.recalculatedProduct, customization]);

  // Use shared util for effective dog stage
  const effectiveDogStage = useMemo(
    () => getEffectiveDogStage(pet, dogStageOverride),
    [dogStageOverride, pet?.dog_stage, pet?.adult_when],
  );

  // Compute hasChanges based on mode
  const hasChanges = useMemo(() => {
    if (mode === 'transition') return true; // Always allow proceeding in transition mode
    if (mode === 'recalculation') return true; // Forced re-calculation always proceeds
    if (mode === 'migration') return true; // Legacy->new suite migration always proceeds

    if (mode === 'health') {
      const originalAllergies = pet?.allergies || [];
      const originalHealthIssues = pet?.health_issues || [];

      const allergiesChanged =
        healthInfo.allergies.length !== originalAllergies.length ||
        healthInfo.allergies.some((a) => !originalAllergies.includes(a));

      const healthIssuesChanged =
        healthInfo.healthIssues.length !== originalHealthIssues.length ||
        healthInfo.healthIssues.some((h) => !originalHealthIssues.includes(h));

      return allergiesChanged || healthIssuesChanged;
    }

    // Weight mode
    return (
      petInfo.weight !== currentPetInfo.weight ||
      petInfo.activity !== currentPetInfo.activity ||
      petInfo.body !== currentPetInfo.body ||
      petInfo.snackLevel !== currentPetInfo.snackLevel ||
      petInfo.adultWeight !== currentPetInfo.adultWeight
    );
  }, [mode, petInfo, currentPetInfo, healthInfo, pet?.allergies, pet?.health_issues]);

  const handleRecalculate = useCallback(() => {
    if (!defaultSubscription?.id || !pet) return;

    dispatch(
      recalculateProductRequest({
        subscriptionId: defaultSubscription.id,
        pet: {
          weight: petInfo.weight,
          activity: petInfo.activity,
          body: petInfo.body,
          allergies: mode === 'health' ? healthInfo.allergies : pet.allergies || [],
          health_issues: mode === 'health' ? healthInfo.healthIssues : pet.health_issues || [],
          dog_stage: effectiveDogStage,
          birth_year: pet.birth_year,
          birth_month: pet.birth_month,
          adult_weight: petInfo.adultWeight, // Only use actual adult_weight; undefined for mixed breeds falls through to adult formula
          snack_level: petInfo.snackLevel,
        },
        currentPreferences: {
          recipes: currentOptions.selectedRecipes,
          pension: currentOptions.pension,
          cycle: currentOptions.cycle,
        },
      }),
    );
  }, [
    dispatch,
    defaultSubscription?.id,
    pet,
    petInfo,
    healthInfo,
    currentOptions,
    mode,
    effectiveDogStage,
  ]);

  const handleSubmit = useCallback(() => {
    if (!defaultSubscription?.id || !lineItem || !selectedVariant) {
      console.warn('[usePetProfileUpdate] handleSubmit: Missing required data', {
        hasSubscription: !!defaultSubscription?.id,
        hasLineItem: !!lineItem,
        hasSelectedVariant: !!selectedVariant,
      });
      return;
    }

    const variantShopifyId = parseInt(selectedVariant.id, 10);
    const intervalCount = customization.cycle;

    // Product kcal of the variant we're swapping to (e.g. "0726" → 726).
    // The backend writes this into food_recommendation on corrective updates.
    const productKcal = parseInt(selectedVariant.metafields?.kcal ?? '', 10);

    const hasWeightChange = petInfo.weight !== currentPetInfo.weight;
    const hasActivityChange = petInfo.activity !== currentPetInfo.activity;
    const hasBodyChange = petInfo.body !== currentPetInfo.body;
    const hasSnackLevelChange = petInfo.snackLevel !== currentPetInfo.snackLevel;
    const hasAdultWeightChange = petInfo.adultWeight !== currentPetInfo.adultWeight;

    const params: UpdatePetAndSwapLineParams = {
      lineId: lineItem.id,
      variantShopifyId,
      quantity: lineItem.quantity,
      ...(Number.isFinite(productKcal) && { productKcal }),
      billingPolicy: {
        interval: 'DAY',
        intervalCount,
      },
      nextBillingDateEpoch: defaultSubscription.nextBillingDateEpoch,
      ...(hasWeightChange && { petWeight: petInfo.weight }),
      ...(hasActivityChange && { petActivity: petInfo.activity }),
      ...(hasBodyChange && { petBody: petInfo.body }),
      ...(hasSnackLevelChange && { petSnackLevel: petInfo.snackLevel }),
      ...(hasAdultWeightChange && { petAdultWeight: petInfo.adultWeight }),
      ...(effectiveDogStage !== pet?.dog_stage && { petDogStage: effectiveDogStage }),
      // Include health data when in health mode
      ...(mode === 'health' && {
        petAllergies: healthInfo.allergies,
        petHealthIssues: healthInfo.healthIssues,
      }),
    };

    setHasSubmitted(true);

    dispatch(
      updatePetAndSwapLineRequest({
        id: defaultSubscription.id,
        params,
      }),
    );
  }, [
    dispatch,
    defaultSubscription,
    lineItem,
    selectedVariant,
    customization,
    petInfo,
    currentPetInfo,
    healthInfo,
    mode,
    effectiveDogStage,
  ]);

  const handleClose = useCallback(() => {
    dispatch(recalculateProductReset());
    setStep('pet-info');
    setPetInfo(currentPetInfo);
    setHealthInfo({
      allergies: pet?.allergies || [],
      healthIssues: pet?.health_issues || [],
    });
    setCustomization(currentOptions);
    setHasSubmitted(false);
    onClose();
  }, [dispatch, onClose, currentPetInfo, currentOptions, pet?.allergies, pet?.health_issues]);

  const goBack = useCallback(() => {
    if (step === 'customize') {
      setStep('pet-info');
    }
  }, [step]);

  const goNext = useCallback(() => {
    if (step === 'pet-info') {
      handleRecalculate();
      setStep('customize');
    }
  }, [step, handleRecalculate]);

  useEffect(() => {
    if (hasSubmitted && !petProfileUpdate.isUpdating && !petProfileUpdate.error) {
      handleClose();
    }
  }, [hasSubmitted, petProfileUpdate.isUpdating, petProfileUpdate.error, handleClose]);

  // Recalculation mode: skip the input step entirely. As soon as we have a
  // subscription + pet, re-run the calculation with the pet's current data and
  // jump straight to the before/after preview.
  const didAutoRecalculate = useRef(false);
  useEffect(() => {
    if ((mode !== 'recalculation' && mode !== 'migration') || didAutoRecalculate.current) return;
    if (!defaultSubscription?.id || !pet) return;

    didAutoRecalculate.current = true;
    handleRecalculate();
    setStep('customize');
  }, [mode, defaultSubscription?.id, pet, handleRecalculate]);

  return useMemo(
    () => ({
      mode,
      step,
      setStep,
      petInfo,
      setPetInfo,
      healthInfo,
      setHealthInfo,
      recalculatedProduct: petProfileUpdate.recalculatedProduct,
      customization,
      setCustomization,
      selectedVariant,
      currentPetInfo,
      currentOptions,
      isRecalculating: petProfileUpdate.isRecalculating,
      isUpdating: petProfileUpdate.isUpdating,
      error: petProfileUpdate.error,
      hasChanges,
      handleRecalculate,
      handleSubmit,
      handleClose,
      goBack,
      goNext,
    }),
    [
      mode,
      step,
      petInfo,
      healthInfo,
      petProfileUpdate.recalculatedProduct,
      customization,
      selectedVariant,
      currentPetInfo,
      currentOptions,
      petProfileUpdate.isRecalculating,
      petProfileUpdate.isUpdating,
      petProfileUpdate.error,
      hasChanges,
      handleRecalculate,
      handleSubmit,
      handleClose,
      goBack,
      goNext,
    ],
  );
}
